import { PrimitiveAtom, useSetAtom } from 'jotai';
import { Alert, AlertColor } from '@mui/material';
import { SnackbarMessage } from '@/types';
import { snackbarMessage as TemplateBuilderSnackbarMessage } from './state';
import {
  RemediationScoreConfig,
  RemediationScoreAreaField,
  RemediationScorePoiField,
  RemediationScoreCofField,
  ReportConfig,
  CardConfig,
  Equation,
  Expression,
  ExpressionType,
  SubtitleConfig,
  Variable,
  MathOperator,
} from '@/__generated__/graphql';
import { REPORT_CONFIG_KEYS } from './constants';

type Props = {
  atom?: PrimitiveAtom<SnackbarMessage>;
  variant?: 'filled' | 'standard' | 'outlined';
};

export const useSnackBarMessage = ({
  atom = TemplateBuilderSnackbarMessage,
  variant = 'filled',
}: Props) => {
  const setSnackbarMessage = useSetAtom(atom);
  const showSnackBar = (message: string, severity: AlertColor) => {
    setSnackbarMessage({
      shouldShow: true,
      content: (
        <Alert sx={{ color: 'white' }} variant={variant} severity={severity}>
          {message && (
            <>
              {message}
              &nbsp;&nbsp;
            </>
          )}
        </Alert>
      ),
    });
  };

  return {
    showSnackBar,
  };
};

export const stripTypenameFromRemediationConfig = (
  remediationScoreConfig: RemediationScoreConfig
): RemediationScoreConfig => {
  return {
    area: remediationScoreConfig.area
      ? {
          fields: remediationScoreConfig.area.fields?.map((field: RemediationScoreAreaField) => ({
            fieldKey: field.fieldKey,
            threshold: field.threshold,
            score: field.score,
          })),
        }
      : null,
    poi: remediationScoreConfig.poi
      ? {
          fields: remediationScoreConfig.poi.fields?.map((field: RemediationScorePoiField) => ({
            poiTemplateKey: field.poiTemplateKey,
            poiScore: field.poiScore,
            subcategory: field.subcategory
              ? {
                  fieldKey: field.subcategory.fieldKey,
                  optionScores: field.subcategory.optionScores,
                }
              : undefined,
          })),
        }
      : null,
    cof: remediationScoreConfig.cof
      ? {
          fields: remediationScoreConfig.cof.fields?.map((field: RemediationScoreCofField) => ({
            fieldKey: field.fieldKey,
            optionScores: field.optionScores,
          })),
        }
      : null,
  };
};

export const convertToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.addEventListener('load', () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Result of file reading was not a string.'));
      }
    });
    reader.addEventListener('error', () => reject(reader.error));
  });

export const orderReportConfig = (config: ReportConfig['configurations']) => {
  const keyIndex: Record<string, number> = REPORT_CONFIG_KEYS.reduce(
    (accumulator, key, index) => ({ ...accumulator, [key]: index }),
    {}
  );

  return [...(config ?? [])].sort((a, b) => {
    const ai = keyIndex[a.key] ?? Infinity;
    const bi = keyIndex[b.key] ?? Infinity;
    return ai - bi;
  });
};

export const removeTypename = <T,>(obj: T): T => {
  if (Array.isArray(obj)) {
    return (obj as unknown[]).map((item) => removeTypename(item)) as unknown as T;
  }

  if (obj !== null && typeof obj === 'object') {
    if (obj instanceof Date) return obj;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { __typename, ...rest } = obj as Record<string, unknown>;

    const cleaned = Object.fromEntries(
      Object.entries(rest).map(([key, value]) => [key, removeTypename(value as unknown)])
    );

    return cleaned as T;
  }

  return obj;
};

export const titleToKey = (title: string) => title.trim().toLowerCase().replace(/\s+/g, '_');

export const keyFromTitle = (title: string) =>
  title
    .trim()
    .toLowerCase()
    .replaceAll(/[^a-z0-9\s-_]/g, '')
    .replaceAll(/\s+/g, '_')
    .replaceAll(/_+/g, '_');

const cleanExpression = (expression: Expression | null | undefined): Expression | null => {
  if (!expression) return null;

  const cleaned = removeTypename(expression);

  if (cleaned.args) {
    cleaned.args = cleaned.args.map(cleanExpression).filter(Boolean) as Expression[];
  }

  if (cleaned.type === ExpressionType.Number) {
    delete cleaned.operator;
    delete cleaned.args;
    delete cleaned.variable;
    return cleaned;
  }

  if (cleaned.type === ExpressionType.Variable) {
    delete cleaned.operator;
    delete cleaned.args;
    delete cleaned.value;
    return cleaned;
  }

  if (cleaned.type === ExpressionType.Operation) {
    const validOperators = Object.values(MathOperator);
    const currentOperator = cleaned.operator as MathOperator;

    if (validOperators.includes(currentOperator)) {
      cleaned.operator = currentOperator;
    } else {
      cleaned.operator = MathOperator.Add;
    }

    cleaned.args = cleaned.args ?? [];

    delete cleaned.value;
    delete cleaned.variable;

    return cleaned;
  }

  return cleaned;
};

const cleanVariable = (variable: Variable | null | undefined): Variable | null => {
  if (!variable) return null;

  return removeTypename(variable);
};

const cleanEquation = (equation: Equation | null | undefined): Equation | null => {
  if (!equation) return null;

  const cleaned = removeTypename(equation);

  cleaned.expression = cleanExpression(cleaned.expression);
  cleaned.variables = cleaned.variables
    ? (cleaned.variables.map(cleanVariable).filter(Boolean) as Variable[])
    : [];

  return cleaned;
};

const cleanSubtitleConfig = (
  subtitleConfig: SubtitleConfig | null | undefined
): SubtitleConfig | null => {
  if (!subtitleConfig) return null;

  return removeTypename(subtitleConfig);
};

const cleanCardConfig = (card: CardConfig | null | undefined): CardConfig | null => {
  if (!card) return null;

  const cleaned = removeTypename(card);

  cleaned.subtitleConfig = cleanSubtitleConfig(cleaned.subtitleConfig);

  return cleaned;
};

export const cleanEquations = (equations: (Equation | null | undefined)[]): Equation[] => {
  return equations.map(cleanEquation).filter(Boolean) as Equation[];
};

export const cleanCardConfigs = (cards: (CardConfig | null | undefined)[]): CardConfig[] => {
  return cards.map(cleanCardConfig).filter(Boolean) as CardConfig[];
};
